import { injectable } from 'inversify';
import { COOKIES_ACCEPTED, MARKETING_ACCEPTED, CONSENT_GRANTED_EVENT, ConsentCategory } from './constants';

type ConsentListener = () => void;

@injectable()
class TrackingConsent {
  /**
   * Static instance for use in `instrumentation-client.ts` where DI is unavailable.
   */
  private static instance = new TrackingConsent();

  static getInstance(): TrackingConsent {
    return TrackingConsent.instance;
  }

  hasConsent(category: ConsentCategory): boolean {
    if (typeof document === 'undefined') return false;
    const name = category === ConsentCategory.Functional ? COOKIES_ACCEPTED : MARKETING_ACCEPTED;
    return document.cookie.split(';').some(
      (c) => c.trim().startsWith(`${ name }=true`),
    );
  }

  /**
   * Wait for consent, then call `fn`. If already consented, calls immediately.
   */
  onConsent(category: ConsentCategory, fn: ConsentListener): void {
    if (this.hasConsent(category)) {
      fn();
      return;
    }
    const handler = (): void => {
      if (this.hasConsent(category)) {
        fn();
        window.removeEventListener(CONSENT_GRANTED_EVENT, handler);
      }
    };
    window.addEventListener(CONSENT_GRANTED_EVENT, handler);
  }

  /**
   * Dispatch after setting consent cookies. Triggers all waiting `onConsent` callbacks.
   */
  notifyConsentGranted(): void {
    window.dispatchEvent(new Event(CONSENT_GRANTED_EVENT));
  }

  /**
   * Set consent cookies for specified categories, then notify.
   *
   * @param categories - Which categories to grant. Defaults to both Functional + Marketing.
   */
  grantConsent(categories: ConsentCategory[] = [ConsentCategory.Functional, ConsentCategory.Marketing]): void {
    if (typeof document === 'undefined') {
      return;
    }

    const maxAge = 365 * 24 * 60 * 60;
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';

    if (categories.includes(ConsentCategory.Functional) && !this.hasConsent(ConsentCategory.Functional)) {
      document.cookie = `${ COOKIES_ACCEPTED }=true; path=/; max-age=${ maxAge }${ secure }`;
    }

    if (categories.includes(ConsentCategory.Marketing) && !this.hasConsent(ConsentCategory.Marketing)) {
      document.cookie = `${ MARKETING_ACCEPTED }=true; path=/; max-age=${ maxAge }${ secure }`;
    }

    this.notifyConsentGranted();
  }

  /**
   * For authenticated users: set consent cookies if missing, then notify.
   */
  ensureConsentForAuthenticatedUser(): void {
    this.grantConsent();
  }
}

export default TrackingConsent;
